import { useContext, useEffect, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  AdvancedSettings,
  LanguageNameSpaces,
  BotType,
  OpenMessage,
  StopType,
} from "@shared/types";
import { isSymbol } from "@shared/types/utils";
import {
  getOptionNameByValue,
  getOptionValueByName,
} from "@shared/features/bots/utils";
import {
  ADVANCED_KEY_TO_INPUT_DISPLAY_NAME,
  CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE,
  DEFAULT_STOP_LIMIT_PRICE_REDUCTION,
  GENERAL_MAX_VALUE,
  MAX_SECONDS_COOLDOWN,
  MAX_SIMULTANEOUS_TRADES_PER_SYMBOL,
  NO_LIMIT_MAX_VALUE,
  STOP_LIMIT_PRICE_REDUCTION_TITLE,
  STOP_TYPE_OPTIONS,
  STOP_TYPE_TITLE,
} from "@shared/features/bots/BotForm/common/constants";
import {
  SECONDS_IN_DAY,
  STOP_TYPE_TO_DISPLAY,
  TIME_STOP_FEATURE_FLAG,
} from "@shared/common/constants";
import { useUser } from "@shared/utils/hooks/misc";
import { round } from "@shared/utils/numbers";
import {
  BotFormType,
  Error,
  OverrideType,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import {
  getAdvancedKeys,
  getDefaultStopType,
  showInvalidOverlapValueMessage,
} from "@shared/features/bots/BotForm/common/utils";
import { useStopType } from "@shared/features/bots/BotForm/common/hooks";
import useFormHelperData from "@shared/features/bots/BotForm/common/hooks/useFormHelperData";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";

interface useAdvancedProps {
  openMessage: OpenMessage;
}

const useAdvanced = ({ openMessage }: useAdvancedProps) => {
  const {
    botFormType,
    botType,
    advancedSettings,
    setAdvancedSettings,
    stopType,
    setStopType,
    stopLimitPriceReduction,
    setStopLimitPriceReduction,
    cancelOnProfitBeforeEntry,
    addError,
    removeError,
    botGeneralData,
    getBotCreateGeneralDataStatus,
    account: formAccount,
    isChannelConfiguration,
    checkedFields,
  } = useContext(BotFormContext)!;
  const [initializedExchange, setInitializedExchange] = useState<
    string | undefined
  >();
  const [operationHoursDrawerOpen, setOperationHoursDrawerOpen] =
    useState(false);
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const account = isSymbol(formAccount) ? undefined : formAccount;
  const { validStopTypeOptions, stopTypeOptions } = useStopType();
  const { getHelperData } = useFormHelperData();
  const alternativeUsdPairsEnabled = checkedFields.has(
    ToggleField.ALTERNATIVE_USD_PAIRS
  );
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const onChange = (key: keyof AdvancedSettings, value: string | undefined) => {
    setAdvancedSettings({ ...advancedSettings, [key]: value });
  };

  const { user } = useUser();
  const advancedSettingsKeys = isChannelConfiguration
    ? []
    : getAdvancedKeys(botType, !!user?.hasFeatureFlag(TIME_STOP_FEATURE_FLAG));

  const isStopTypeError =
    !!stopType &&
    !isSymbol(stopType) &&
    !validStopTypeOptions.find((option) => option.value === stopType);

  const getShouldInitialize = () => {
    const formInitialized =
      !!botGeneralData &&
      !getBotCreateGeneralDataStatus.isFetching &&
      !!account;
    const formValuesChanged =
      initializedExchange && account?.exchange !== initializedExchange;
    return (
      formInitialized &&
      validStopTypeOptions?.length &&
      (!stopType || formValuesChanged || (isStopTypeError && !isBulkCreate))
    );
  };

  const setStopTypeWithPriceReduction = (stopType?: StopType) => {
    setStopType(stopType);
    const isStopLimit = stopType === StopType.LIMIT;
    if (isStopLimit && !stopLimitPriceReduction) {
      setStopLimitPriceReduction(DEFAULT_STOP_LIMIT_PRICE_REDUCTION.toString());
    } else if (!isStopLimit && stopLimitPriceReduction) {
      setStopLimitPriceReduction(undefined);
    }
  };

  useEffect(() => {
    if (stopType && !validStopTypeOptions?.length) {
      setStopTypeWithPriceReduction(undefined);
    } else if (getShouldInitialize()) {
      const defaultStopType = getDefaultStopType(validStopTypeOptions);
      if (defaultStopType) setStopTypeWithPriceReduction(defaultStopType);
      setInitializedExchange(account?.exchange);
    }
  }, [isStopTypeError, validStopTypeOptions]);

  const getTimeConversionSuffix = (value?: string) => {
    const parsedValue = value === undefined ? 0 : parseFloat(value);
    const valueInMinutes = parsedValue / 60;
    const valueInHours = valueInMinutes / 60;
    const valueInDays = valueInHours / 24;
    const iterateData: [number, TranslationKeys<LanguageNameSpaces.BOTS>][] = [
      [valueInDays, "time_units.suffix_days"],
      [valueInHours, "time_units.suffix_hours"],
      [valueInMinutes, "time_units.suffix_minutes"],
    ];
    let suffix = ``;
    iterateData.some(([value, suffixKey]) => {
      if (value > 1 || suffixKey === "time_units.suffix_minutes") {
        const roundedValue = round(value, 1);
        suffix = t(suffixKey, {
          approx: roundedValue === value ? "" : "~",
          value: roundedValue,
        });
        return true;
      }
    });
    return suffix;
  };

  const inputsData = advancedSettingsKeys.map((advancedKey) => {
    const isTimeInput = TIME_BASED_ADVANCED_KEYS.has(advancedKey);
    const value = advancedSettings[advancedKey];
    const isMultiValue = isSymbol(value);
    let maxValue = GENERAL_MAX_VALUE;
    const isNumTradePerSymbol =
      advancedKey === "number_of_simultaneous_trades_per_symbol";
    const isDurationInput =
      advancedKey === "auto_cancel_timeout_seconds" ||
      advancedKey === "time_stop_seconds";
    const minimumSeconds = MIN_AUTO_CANCEL_TIMEOUT_SECONDS;
    if (isDurationInput) maxValue = MAX_AUTO_CANCEL_TIMEOUT_SECONDS;
    else if (isNumTradePerSymbol) maxValue = MAX_SIMULTANEOUS_TRADES_PER_SYMBOL;
    else if (isTimeInput) maxValue = MAX_SECONDS_COOLDOWN;
    else if (ABSOLUTE_MAX_VALUES_ADVANCED_KEYS.has(advancedKey))
      maxValue = NO_LIMIT_MAX_VALUE;
    const parsedValue = value && !isMultiValue ? parseFloat(value) : 0;

    let errorMessage: string | undefined;
    if (!isMultiValue) {
      if (value) {
        if (isDurationInput) {
          const isTooHigh = parsedValue > MAX_AUTO_CANCEL_TIMEOUT_SECONDS;
          const isTooLow = parsedValue < minimumSeconds;
          if (isTooHigh) {
            errorMessage = "Value should be 30 days or less";
          } else if (isTooLow) {
            errorMessage = `Value should be ${minimumSeconds} seconds or more`;
          }
        } else if (parsedValue > maxValue) {
          errorMessage = `Value should be ${maxValue} or less`;
        }
      } else if (isNumTradePerSymbol) {
        errorMessage = "You must provide a value for this parameter";
      }
    }

    let infoMessage: string | undefined;
    if (
      advancedKey === "cooldown_between_opening_trades_per_symbol" &&
      alternativeUsdPairsEnabled
    ) {
      infoMessage =
        "When 'Alternative USD Pairs' is enabled, it's recommended to set 'Cooldown Between Trades per Symbol' to at least 60s to prevent duplicate trades.";
    }

    return {
      bordered: true,
      placeholder: isMultiValue
        ? t("labels.multi")
        : t("messages.info.no_limit"),
      precision: advancedKey === "min_symbol_price" ? 8 : 0,
      title: t(ADVANCED_KEY_TO_INPUT_DISPLAY_NAME[advancedKey]),
      key: advancedKey,
      onChange: (value: string) => onChange(advancedKey, value),
      value: isMultiValue ? "" : value,
      suffix:
        isTimeInput && !isMultiValue
          ? getTimeConversionSuffix(value)
          : undefined,
      error: errorMessage,
      infoMessage,
      helperData: getHelperData(advancedKey),
    };
  });

  const autoCancelTimeoutError = inputsData.find(
    (input) => input.key === "auto_cancel_timeout_seconds"
  )?.error;

  const getStopLimitPriceReductionData = () => {
    const shouldDisplay =
      stopType === StopType.LIMIT && !!stopTypeOptions?.length;
    const isMultiValue = isSymbol(stopLimitPriceReduction);
    const parsedValue =
      stopLimitPriceReduction && !isMultiValue
        ? parseFloat(stopLimitPriceReduction)
        : 0;
    const isError = isMultiValue
      ? undefined
      : parsedValue > MAX_STOP_LIMIT_PRICE_REDUCTION;
    const isWarning = isMultiValue
      ? undefined
      : parsedValue < MIN_STOP_LIMIT_PRICE_REDUCTION;
    return {
      shouldDisplay,
      bordered: true,
      placeholder: isMultiValue
        ? t("labels.multi")
        : t("messages.info.no_limit"),
      precision: 1,
      title: t(STOP_LIMIT_PRICE_REDUCTION_TITLE),
      onChange: setStopLimitPriceReduction,
      value: isMultiValue ? "" : stopLimitPriceReduction,
      suffix: "%",
      error: isError
        ? `Value should be less than ${MAX_STOP_LIMIT_PRICE_REDUCTION}`
        : undefined,
      warning: isWarning
        ? `A value that is too small might result in the limit order being skipped in cases of a quick price drop. Consider entering a value above ${MIN_STOP_LIMIT_PRICE_REDUCTION}%.`
        : undefined,
    };
  };

  const getCancelOnProfitBeforeEntryData = () => {
    let value: OverrideType | undefined = undefined;
    if (!isSymbol(cancelOnProfitBeforeEntry))
      value = value ? OverrideType.PERSONAL : OverrideType.PERSONAL;
    return {
      value,
      title: t(CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE),
    };
  };

  const cancelOnProfitBeforeEntryData = getCancelOnProfitBeforeEntryData();

  const getStopTypeData = () => {
    const isMultiValue = isSymbol(stopType);
    let value: string | undefined = STOP_TYPE_TO_DISPLAY[StopType.LIMIT];
    if (isSymbol(stopType)) value = undefined;
    else if (stopType)
      value = getOptionNameByValue(stopType, STOP_TYPE_OPTIONS);
    return {
      shouldDisplay: !!stopTypeOptions?.length,
      options: stopTypeOptions?.map((option) => option.name),
      invalidOptions: stopTypeOptions
        ?.filter((option) => option.isInvalid)
        .map((option) => option.name),
      onChange: (stopTypeDisplay?: string) => {
        const stopType = getOptionValueByName(
          stopTypeDisplay,
          STOP_TYPE_OPTIONS
        ) as StopType;
        if (
          !validStopTypeOptions?.find((option) => option.value === stopType)
        ) {
          showInvalidOverlapValueMessage("stopType", openMessage, t);
        } else {
          setStopTypeWithPriceReduction(stopType);
        }
      },
      value,
      title: t(STOP_TYPE_TITLE),
      isDropdown: botFormType === BotFormType.BULK_EDIT,
      placeholder: isMultiValue ? t("labels.multi") : undefined,
      error: isStopTypeError
        ? t("messages.errors.stop_type_unavailable")
        : undefined,
      helperData: getHelperData("stopType"),
    };
  };

  const stopTypeData = getStopTypeData();
  const stopLimitPriceReductionData = getStopLimitPriceReductionData();

  const hasError =
    inputsData.some((input) => !!input.error) ||
    stopLimitPriceReductionData.error ||
    stopTypeData.error;

  useEffect(() => {
    if (hasError) addError(Error.GENERAL_ADVANCED);
    else removeError(Error.GENERAL_ADVANCED);
  }, [hasError]);

  return {
    inputsData,
    stopTypeData,
    stopLimitPriceReductionData,
    cancelOnProfitBeforeEntryData,
    showAccountConfiguration:
      botFormType !== BotFormType.BULK_EDIT &&
      botFormType !== BotFormType.BULK_CREATE &&
      botType !== BotType.TRADING_VIEW_SIGNALS &&
      !isChannelConfiguration,
    operationHoursDrawerOpen,
    setOperationHoursDrawerOpen,
    autoCancelTimeoutError,
  };
};

export default useAdvanced;

const TIME_BASED_ADVANCED_KEYS: Set<keyof AdvancedSettings> = new Set([
  "cooldown_between_opening_trades",
  "cooldown_after_trade_close",
  "cooldown_between_opening_trades_per_symbol",
]);
const ABSOLUTE_MAX_VALUES_ADVANCED_KEYS: Set<keyof AdvancedSettings> = new Set([
  "min_symbol_price",
  "min_symbol_volume",
  "concurrent_max_usd_invested",
]);
const MAX_STOP_LIMIT_PRICE_REDUCTION = 10;
const MIN_STOP_LIMIT_PRICE_REDUCTION = 1;
const MAX_AUTO_CANCEL_TIMEOUT_SECONDS = 30 * SECONDS_IN_DAY;
const MIN_AUTO_CANCEL_TIMEOUT_SECONDS = 60;
